import Autocomplete from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { wz } from '../../components/wizard-tokens';
import { WizChip } from '../../components/wizard-ui';
import { WIZARD_INDUSTRIES } from '../../industries';
import { industrySuggestions, MAX_INDUSTRIES, sanitizeIndustries } from './industry-logic';

interface Props {
  values: string[];
  onChange: (values: string[]) => void;
}

/** Zeile „Branche“: Mehrfachauswahl mit Vorschlägen beim Tippen, gewählte Branchen als Chips (wie bei LinkedIn). */
const IndustryPicker: React.FC<Props> = ({ values, onChange }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const full = values.length >= MAX_INDUSTRIES;

  return (
    <Autocomplete<string, true>
      multiple
      value={values}
      options={WIZARD_INDUSTRIES as string[]}
      onChange={(_, next) => onChange(sanitizeIndustries(next))}
      filterOptions={(options, state) => industrySuggestions(state.inputValue, values, options)}
      getOptionDisabled={() => full}
      disableCloseOnSelect
      openOnFocus
      autoHighlight
      noOptionsText={t('wizCandIndustryNone')}
      className={classes.root}
      slotProps={{ paper: { className: classes.paper } }}
      renderTags={(selected, getTagProps) =>
        selected.map((label, index) => {
          const { key, onDelete } = getTagProps({ index });
          return (
            <WizChip key={key} variant="blue" onRemove={() => onDelete({} as React.SyntheticEvent)}>
              {label}
            </WizChip>
          );
        })
      }
      renderInput={params => (
        <div ref={params.InputProps.ref} className={classes.box}>
          {params.InputProps.startAdornment}
          <InputBase
            inputProps={params.inputProps}
            id={params.id}
            disabled={params.disabled}
            fullWidth={false}
            className={classes.input}
            placeholder={full ? t('wizCandIndustryMax', { count: MAX_INDUSTRIES }) : t('wizCandIndustryPlaceholder')}
          />
        </div>
      )}
    />
  );
};

const useStyles = makeStyles()({
  root: { flex: 1, minWidth: 0 },
  box: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    background: wz.page,
    borderRadius: 10,
    padding: '6px 10px',
    minHeight: 39,
    boxSizing: 'border-box',
  },
  input: {
    flex: 1,
    minWidth: 160,
    fontSize: 14,
    fontFamily: 'inherit',
    color: wz.text,
    '& input': { padding: '2px 0' },
  },
  paper: { boxShadow: wz.shadowMenu, borderRadius: 10, fontSize: 14 },
});

export default IndustryPicker;
